import { useEffect } from 'react';

import { useToast } from '@/components/feedback/toast';
import { useCountdown } from '@/hooks/use-countdown';
import { ApiError } from '@/lib/api/errors';

import { AUTH_ERROR_CODES } from './constants';

const LOCK_KEY = 'auth-lockout';

/** Whole minutes remaining until lockout expiry, or null if not a lockout error. */
export function lockoutMinutesRemaining(error: unknown, now = Date.now()): number | null {
  if (!(error instanceof ApiError) || error.code !== AUTH_ERROR_CODES.ACCOUNT_TEMPORARILY_LOCKED)
    return null;
  const iso = error.details?.lockoutExpiresAt as string | undefined;
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 60_000));
}

/** Drives a sticky, keyed lockout toast (minute text updates live) and reports locked state. */
export function useLockoutNotice(error: unknown): { isLocked: boolean } {
  const toast = useToast();
  const iso =
    error instanceof ApiError && error.code === AUTH_ERROR_CODES.ACCOUNT_TEMPORARILY_LOCKED
      ? (error.details?.lockoutExpiresAt as string | undefined)
      : undefined;
  const deadline = iso ? new Date(iso).getTime() : null;
  const secondsLeft = useCountdown(deadline);
  const minutes = deadline ? Math.ceil(secondsLeft / 60) : 0;
  const isLocked = secondsLeft > 0;

  useEffect(() => {
    if (isLocked) {
      // Figma's lockout toast is warning/amber-toned, not error/red.
      toast.warning({
        key: LOCK_KEY,
        duration: 0,
        title: `Account temporarily locked. Try again after ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`,
      });
    } else {
      toast.dismiss(LOCK_KEY);
    }
  }, [isLocked, minutes, toast]);

  return { isLocked };
}
